import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { ZodType } from "zod";

/**
 * The data router's replacement for `validateSearch` + `Route.useSearch()`.
 *
 * The file-based router parsed the query string once per route and handed the
 * page a typed object; react-router hands out raw `URLSearchParams`. The schema
 * is the same one the route carried, so every page keeps its defaults and its
 * types — `.catch()` fills an absent or unparsable value exactly as before.
 *
 * One difference worth knowing: the old router JSON-parsed each value, so a
 * numeric id arrived as a number. Here everything arrives as a string, which is
 * why the schemas that cared already coerce with `z.union([...]).transform(String)`.
 */
export function useTypedSearch<T>(schema: ZodType<T>): T {
  const [params] = useSearchParams();
  const raw = params.toString();
  return useMemo(
    () => schema.parse(Object.fromEntries(new URLSearchParams(raw).entries())),
    [raw, schema],
  );
}
